/**
 * Saved monthly incentive calculation snapshots (brand pool + staff net pay).
 * Doc id: `{year}-{monthIndex}-{branchFilter}` e.g. `2026-5-Main`.
 */
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

import {
  computeStaffPoolPctIncentive,
  getStaffPoolPctPlanForPerson,
} from "../config/employeeStaffPoolPctPlans";

export const COL_MONTHLY_INCENTIVE_SNAPSHOTS = "monthly_incentive_snapshots";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function monthlyIncentiveSnapshotDocId(year, monthIndex, branchFilter) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const branch = String(branchFilter || "Main");
  return `${y}-${mi}-${branch}`;
}

/** Firestore rejects nested arrays — store person totals as `{ name, amount, totalPct?, staffPoolPct?, othersTotal? }[]`. */
export function personTotalsForFirestore(personTotals) {
  return (personTotals || [])
    .map((entry) => {
      if (Array.isArray(entry)) {
        return { name: String(entry[0] || "").trim(), amount: Number(entry[1]) || 0 };
      }
      const row = {
        name: String(entry?.name || "").trim(),
        amount: Number(entry?.amount ?? entry?.amt ?? 0) || 0,
      };
      if (entry?.totalPct != null && Number.isFinite(Number(entry.totalPct))) {
        row.totalPct = Number(entry.totalPct);
      }
      if (entry?.staffPoolPct) {
        row.staffPoolPct = true;
        row.othersTotal = Number(entry.othersTotal) || 0;
      }
      return row;
    })
    .filter((row) => row.name);
}

/** Normalize loaded person totals (legacy tuple or object rows). */
export function normalizePersonTotalsFromFirestore(personTotals) {
  return personTotalsForFirestore(personTotals);
}

/**
 * Save computed incentive snapshot for one month + branch.
 * @param {object} snapshot — built on MonthlyIncentivePage after calculation
 */
export async function saveMonthlyIncentiveSnapshot(snapshot) {
  const year = Number(snapshot.year);
  const monthIndex = Math.max(0, Math.min(11, Number(snapshot.monthIndex)));
  const branchFilter = String(snapshot.branchFilter || "Main");
  const docId = monthlyIncentiveSnapshotDocId(year, monthIndex, branchFilter);

  const payload = {
    ...snapshot,
    year,
    monthIndex,
    branchFilter,
    personTotals: personTotalsForFirestore(snapshot.personTotals),
    monthLabel: MONTH_LABELS[monthIndex],
    title: `Monthly incentive — ${MONTH_LABELS[monthIndex]} ${year} — ${branchFilter}`,
    generatedAt: snapshot.generatedAt || new Date().toISOString(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, COL_MONTHLY_INCENTIVE_SNAPSHOTS, docId), payload, { merge: true });
  return { id: docId, ...payload };
}

/** Load saved incentive snapshot for month + branch. */
export async function loadMonthlyIncentiveSnapshot({ year, monthIndex, branchFilter = "Main" }) {
  const docId = monthlyIncentiveSnapshotDocId(year, monthIndex, branchFilter);
  const snap = await getDoc(doc(db, COL_MONTHLY_INCENTIVE_SNAPSHOTS, docId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Mark a staff allocation WhatsApp Send click (blue-tick) on the saved snapshot.
 * @returns {Promise<{ key: string, sentAt: string, whatsappSentStaffLines: Record<string, string> }>}
 */
export async function markStaffLineWhatsAppSent({ year, monthIndex, branchFilter = "Main", lineKey }) {
  const key = String(lineKey || "").trim();
  if (!key) throw new Error("Missing staff line key");
  const docId = monthlyIncentiveSnapshotDocId(year, monthIndex, branchFilter);
  const ref = doc(db, COL_MONTHLY_INCENTIVE_SNAPSHOTS, docId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("No saved incentive snapshot for this month/branch");
  const prev = snap.data()?.whatsappSentStaffLines;
  const map = prev && typeof prev === "object" && !Array.isArray(prev) ? { ...prev } : {};
  const sentAt = new Date().toISOString();
  map[key] = sentAt;
  await setDoc(
    ref,
    { whatsappSentStaffLines: map, whatsappSentUpdatedAt: serverTimestamp() },
    { merge: true }
  );
  return { key, sentAt, whatsappSentStaffLines: map };
}

function parseNum(v) {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function normKey(v) {
  return String(v ?? "").trim().toLowerCase();
}

function staffShareOfBrandAmount(amount, pct, sumPct) {
  const amt = Number(amount) || 0;
  const p = Math.max(0, parseNum(pct));
  const sp = Math.max(0, parseNum(sumPct));
  if (amt <= 0 || p <= 0 || sp <= 0) return 0;
  if (sp > 100) return (amt * p) / sp;
  return (amt * p) / 100;
}

function staffLineMatchesEmployee(line, employeeId, displayName) {
  const eid = normKey(employeeId);
  const lineEid = normKey(line?.employeeId);
  if (eid && lineEid) return lineEid === eid;
  const name = normKey(displayName);
  const lineName = normKey(line?.name);
  return Boolean(name && lineName && lineName === name);
}

function personTotalMatchesEmployee(entry, employeeId, displayName) {
  const eid = normKey(employeeId);
  const name = normKey(displayName);
  const entryName = normKey(entry?.name);
  if (name && entryName && entryName === name) return true;
  if (eid && entryName && entryName === eid) return true;
  return false;
}

function buildSumPctByBrand(staffLines) {
  const sumPctByBrand = {};
  for (const L of staffLines || []) {
    const bid = String(L.brandId || "").trim();
    if (!bid) continue;
    sumPctByBrand[bid] = (sumPctByBrand[bid] || 0) + Math.max(0, parseNum(L.pct));
  }
  return sumPctByBrand;
}

function buildMyBrandOeOrdRows(brandRows, myLines) {
  const rowByBrand = Object.fromEntries((brandRows || []).map((r) => [r.brandId, r]));
  const pctByBrand = {};
  for (const L of myLines) {
    const bid = String(L.brandId || "").trim();
    if (!bid) continue;
    pctByBrand[bid] = (pctByBrand[bid] || 0) + Math.max(0, parseNum(L.pct));
  }
  const out = [];
  for (const [brandId, pct] of Object.entries(pctByBrand)) {
    const sr = rowByBrand[brandId];
    if (!sr) continue;
    out.push({
      brandId,
      pct,
      oeSale: Number(sr.oeSale) || 0,
      ordSale: Number(sr.ordSale) || 0,
    });
  }
  out.sort((a, b) => a.brandId.localeCompare(b.brandId));
  return out;
}

/**
 * Map a saved monthly_incentive_snapshots doc to Employee Portal earnings shape.
 * No live import, config, or precalc reads — snapshot only.
 */
export function buildEmployeePortalIncentiveFromSnapshot(
  saved,
  { year, monthIndex, employeeId, displayName }
) {
  const monthLabel =
    saved?.monthLabel ||
    saved?.periodLabel ||
    new Date(Number(year), Number(monthIndex), 1).toLocaleString("en-IN", {
      month: "long",
      year: "numeric",
    });

  if (!saved) {
    return {
      monthLabel,
      grandTotal: 0,
      myLines: [],
      totalFinal: 0,
      myBrandOeOrd: [],
      totalBranchOe: 0,
      totalBranchOrd: 0,
      configStaffRowsForMe: 0,
      specialGroupLines: [],
      specialGroupTotal: 0,
      note: "No incentive snapshot saved for this month yet. Ask HR to save from the Monthly Incentive page.",
      snapshotMissing: true,
    };
  }

  const brandRows = saved.brandRows || [];
  const brandById = Object.fromEntries(brandRows.map((r) => [r.brandId, r]));
  const allStaffLines = saved.staffLines || [];
  const sumPctByBrand = buildSumPctByBrand(allStaffLines);
  const eid = normKey(employeeId);

  const myLinesRaw = allStaffLines.filter((L) => staffLineMatchesEmployee(L, employeeId, displayName));
  const myLines = myLinesRaw.map((L) => {
    const sr = L.brandId ? brandById[L.brandId] : null;
    const sumPct = L.brandId ? sumPctByBrand[L.brandId] ?? 0 : 0;
    return {
      ...L,
      oeSale: Number(sr?.oeSale) || 0,
      ordSale: Number(sr?.ordSale) || 0,
      myOeIncentive: staffShareOfBrandAmount(sr?.oeIncentive, L.pct, sumPct),
      myOrdIncentive: staffShareOfBrandAmount(sr?.ordIncentive, L.pct, sumPct),
    };
  });

  const specialGroupLines = (saved.specialGroupLines || []).filter(
    (G) => normKey(G?.employeeId) === eid
  );
  const specialGroupTotal = specialGroupLines.reduce(
    (a, G) => a + (Number(G.incentiveAmount) || 0),
    0
  );

  const personTotals = normalizePersonTotalsFromFirestore(saved.personTotals);
  const personRow = personTotals.find((p) => personTotalMatchesEmployee(p, employeeId, displayName));
  const poolPlan = getStaffPoolPctPlanForPerson({ name: displayName, employeeId });
  const savedPool =
    (saved.staffPoolPctOverrides || []).find(
      (o) =>
        getStaffPoolPctPlanForPerson({ name: o?.name, employeeId: o?.employeeId }) &&
        (personTotalMatchesEmployee(o, employeeId, displayName) ||
          getStaffPoolPctPlanForPerson({ name: displayName, employeeId }))
    ) || null;

  let myLinesOut = myLines;
  let specialGroupLinesOut = specialGroupLines;
  let specialGroupTotalOut = specialGroupTotal;
  let staffPoolPct = null;
  let totalFinal =
    personRow != null
      ? Number(personRow.amount) || 0
      : myLines.reduce((a, L) => a + (Number(L.finalAmt) || 0), 0) + specialGroupTotal;

  if (poolPlan) {
    if (savedPool) {
      staffPoolPct = {
        ratePct: Number(savedPool.ratePct) || poolPlan.ratePct,
        othersTotal: Number(savedPool.othersTotal) || 0,
        amount: Number(savedPool.amount) || 0,
      };
      totalFinal = staffPoolPct.amount;
    } else if (personRow?.staffPoolPct) {
      staffPoolPct = {
        ratePct: Number(personRow.totalPct) || poolPlan.ratePct,
        othersTotal: Number(personRow.othersTotal) || 0,
        amount: Number(personRow.amount) || 0,
      };
      totalFinal = staffPoolPct.amount;
    } else {
      let others = 0;
      for (const L of allStaffLines) {
        if (getStaffPoolPctPlanForPerson({ name: L.name, employeeId: L.employeeId })) continue;
        others += Number(L.finalAmt) || 0;
      }
      for (const G of saved.specialGroupLines || []) {
        if (getStaffPoolPctPlanForPerson({ name: G.employeeName, employeeId: G.employeeId })) continue;
        others += Number(G.incentiveAmount) || 0;
      }
      const amount = computeStaffPoolPctIncentive(others, poolPlan.ratePct);
      staffPoolPct = { ratePct: poolPlan.ratePct, othersTotal: others, amount };
      totalFinal = amount;
    }
    myLinesOut = [];
    specialGroupLinesOut = [];
    specialGroupTotalOut = 0;
  }

  const myBrandOeOrd = buildMyBrandOeOrdRows(brandRows, myLinesOut);
  const totalBranchOe = myBrandOeOrd.reduce((a, r) => a + r.oeSale, 0);
  const totalBranchOrd = myBrandOeOrd.reduce((a, r) => a + r.ordSale, 0);

  let note;
  if (staffPoolPct) {
    note = `Incentive is ${staffPoolPct.ratePct}% of all other staff net pay.`;
  } else if (!myLinesOut.length && !specialGroupLinesOut.length) {
    note = "No incentive rows for your Employee ID in the saved snapshot for this month.";
  }

  return {
    monthLabel,
    grandTotal: Number(saved.summary?.grandTotal) || 0,
    myLines: myLinesOut,
    totalFinal,
    myBrandOeOrd,
    totalBranchOe,
    totalBranchOrd,
    configStaffRowsForMe: myLinesOut.length || (staffPoolPct ? 1 : 0),
    specialGroupLines: specialGroupLinesOut,
    specialGroupTotal: specialGroupTotalOut,
    staffPoolPct,
    note,
    snapshotId: saved.id,
    snapshotGeneratedAt: saved.generatedAt,
  };
}

/** Employee Portal — single Firestore read from monthly_incentive_snapshots. */
export async function fetchEmployeePortalIncentive({
  year,
  monthIndex,
  branchFilter = "Main",
  employeeId,
  displayName = "",
}) {
  const saved = await loadMonthlyIncentiveSnapshot({ year, monthIndex, branchFilter });
  return buildEmployeePortalIncentiveFromSnapshot(saved, {
    year,
    monthIndex,
    employeeId,
    displayName,
  });
}
